import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Send, Package } from 'lucide-react';
import config from '../config';

const API_BASE = config.apiUrl.replace(/\/+$/, '');

const PackingFeedback = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const picking = params.get('picking') || '';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setError('Please describe the issue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/feedback/${token}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: description.trim(),
          reference: picking,
          name: name.trim(),
          priority,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit feedback');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center text-gray-500">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-400" />
          <p className="font-semibold">Invalid link</p>
          <p className="text-sm mt-1">This feedback page requires a valid token.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
          <h2 className="text-lg font-bold text-gray-900">Thank you!</h2>
          <p className="text-gray-600 mt-2 text-sm">
            Your feedback for <span className="font-semibold">{picking}</span> has been submitted.
            The team will review it shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-lg w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
            <Package className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Packing Feedback</h1>
            {picking && (
              <p className="text-sm text-gray-500">
                Order: <span className="font-mono font-semibold text-gray-700">{picking}</span>
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Optional — helps us follow up"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What's wrong? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue — e.g. wrong box size, missing item, incorrect dimensions..."
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <div className="flex gap-2">
              {[
                { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700 border-gray-300' },
                { value: 'medium', label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-300' },
                { value: 'high', label: 'High', color: 'bg-red-50 text-red-700 border-red-300' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPriority(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    priority === opt.value
                      ? `${opt.color} ring-2 ring-offset-1 ring-amber-400`
                      : 'bg-white text-gray-400 border-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 px-4 text-sm transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <>Submitting...</>
            ) : (
              <><Send className="h-4 w-4" /> Submit Feedback</>
            )}
          </button>
        </form>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Feedback is reviewed by the Shippy team
        </p>
      </div>
    </div>
  );
};

export default PackingFeedback;
